import { TextButton } from 'components/v2/buttons';
import { ErrorAlertPanel } from 'components/v2/compiler/panels/Styles.styled';
import WizardCheckbox from 'components/v2/epd-wizard-inputs/WizardCheckbox';
import WizardSelectComponent from 'components/v2/epd-wizard-inputs/WizardSelect';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { LabelGroup } from 'components/v2/form/styled';
import { HelpBox } from 'components/v2/help-boxes';
import { StatusAttentionIcon } from 'components/v2/icons';
import TooltipHelpIcon from 'components/v2/icons/TooltipHelpIcon';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useEpdDataSourceOptions } from 'services/api/queries';
import { EPDDataSourceModel, EPDModel } from 'services/EpdClient';
import styled from 'styled-components';
import { DoubleFieldGrid, FieldContainer, FieldPanelInput } from 'styles/v2/Styles.styled';
import { DataSourceOptionModel, Option } from 'types/types';

interface Props {
  epd: EPDModel;
  onChangeEpd(name: string, value: any): Promise<void>;
  initialDataSources: EPDDataSourceModel[];
  onSave(newValues: EPDDataSourceModel[]): void;
}

const DataSourcesCopySection: React.FC<Props> = ({ epd, onChangeEpd, initialDataSources, onSave }) => {
  const { t } = useTranslation();
  const [rowDataSourceValues, setDataSourceRowValues] = useState<EPDDataSourceModel[]>(initialDataSources);
  const dataSourceOptionsSource = useEpdDataSourceOptions().data ?? [];
  const dataSourceOptions =
    dataSourceOptionsSource
      ?.filter((x) => !x.isDisabled)
      .filter((x) => x.dataSourceVersions && x.dataSourceVersions.some((v) => !v.isDisabled))
      .map((x: DataSourceOptionModel) => {
        return {
          value: x.id,
          label: x.name,
        } as Option;
      }) ?? [];

  const getDataSourceVersionOptions = (dataSourceId?: string) => {
    if (!dataSourceId) {
      return [] as Option[];
    }

    const dataSource = dataSourceOptionsSource?.find(
      (x: DataSourceOptionModel) => x.id.toLowerCase() === dataSourceId.toLowerCase()
    );

    return dataSource?.dataSourceVersions;
  };

  const createDataSourceRow = () => {
    const newRowValues: EPDDataSourceModel[] = [
      ...rowDataSourceValues,
      {
        id: undefined,
        dataSource: { id: undefined, name: '' },
        dataSourceVersion: { id: undefined, name: '' },
        isMain: false,
      },
    ];
    setDataSourceRowValues(newRowValues);
  };

  const deleteDataSourceRow = (index: number) => {
    const newValues = rowDataSourceValues.filter((_, i) => i !== index);
    setDataSourceRowValues(newValues);
    onSave(newValues);
  };

  // Ed: This copy-paste from back-end hurts my soul
  const isDataSourceComplete =
    (!epd.isOtherDatabaseUsedAsDatasource || !isNullOrWhiteSpace(epd.otherDatabaseUsedAsDatasourceDescription)) &&
    rowDataSourceValues.every((x) => !(x.dataSource?.isDisabled ?? false) && !(x.dataSourceVersion?.isDisabled ?? false)) &&
    (!(epd.pcr?.isConstruction ?? false) || rowDataSourceValues.some((x) => x.dataSource?.id && x.dataSourceVersion?.id));

  return (
    <>
      <FieldContainer>
        <FieldPanelInput>
          <LabelGroup>
            <span style={{ fontWeight: 'bold' }}>DATA SOURCES USED FOR THIS DATA SET</span>
            <TooltipHelpIcon content={t('epdWizard.wizardStep2.tooltips.dataSources')} position="right" />
            {!isDataSourceComplete && <StatusAttentionIcon />}
          </LabelGroup>
          <HelpBox>Select main data source and specific version of the background database used for the EPD.</HelpBox>
        </FieldPanelInput>
      </FieldContainer>
      <DoubleFieldGrid style={{ marginTop: 22 }}>
        {rowDataSourceValues.map((item, index) => (
          <React.Fragment key={index}>
            <FieldPanelInput>
              <WizardSelectComponent
                options={dataSourceOptions}
                value={item?.dataSource?.id ? { value: item?.dataSource?.id, label: item?.dataSource?.name } : null}
                error={item?.dataSource?.isDisabled ? 'Database is disabled. Please select a valid database.' : undefined}
                errorDisplayType="border"
                isClearable
                placeholder={t('placeholder.defaultSelectDatabase')}
                name={'DataSource'}
                label="Database"
                tooltip="Background database used for the EPD."
                tooltipPosition="right"
                required
                onChanged={(propName: string, id: string) => {
                  const newDataSources = rowDataSourceValues.map((d, i) =>
                    i === index
                      ? {
                          ...d,
                          dataSource: { id: id, name: dataSourceOptionsSource.find((ds) => ds.id === id)?.name },
                          dataSourceVersion: { id: undefined, name: '' },
                        }
                      : d
                  );
                  setDataSourceRowValues(newDataSources);
                  onSave(newDataSources);
                }}
              />

              <CheckBoxGroup>
                <WizardCheckbox
                  inputId={`dataSourceMain${index}`}
                  label="Set as main database"
                  name="isMain"
                  value={item?.isMain}
                  onChanged={(_, value) => {
                    const newDataSources = rowDataSourceValues.map((d, i) => ({ ...d, isMain: value && i === index }));
                    setDataSourceRowValues(newDataSources);
                    onSave(newDataSources);
                  }}
                />
              </CheckBoxGroup>
            </FieldPanelInput>

            <DataSourceVersionColumn>
              <WizardSelectComponent
                options={getDataSourceVersionOptions(item.dataSource?.id)?.filter((x) => !x.isDisabled) ?? []}
                value={
                  item?.dataSourceVersion?.id
                    ? { value: item?.dataSourceVersion?.id, label: item?.dataSourceVersion?.name }
                    : null
                }
                isClearable
                error={
                  item?.dataSourceVersion?.isDisabled ? 'Version is disabled. Please select a valid version.' : undefined
                }
                errorDisplayType="border"
                name="DataSourceVersion"
                label="Version"
                tooltip="Select specific version of the background database"
                required
                placeholder={t('placeholder.defaultSelectDatabaseVersion')}
                onChanged={(propName: string, id: string) => {
                  const newDataSources = rowDataSourceValues.map((d, i) =>
                    i === index
                      ? {
                          ...d,
                          dataSourceVersion: {
                            id: id,
                            name: getDataSourceVersionOptions(item.dataSource?.id)?.find((dv) => dv.value === id)?.label,
                          },
                        }
                      : d
                  );
                  setDataSourceRowValues(newDataSources);
                  onSave(newDataSources);
                }}
              />
              {rowDataSourceValues.length > 1 && !item.isMain && (
                <TextButton
                  icon="pi pi-trash"
                  style={{ alignSelf: 'center', marginLeft: '0.5rem' }}
                  onClick={() => deleteDataSourceRow(index)}
                  text
                />
              )}
            </DataSourceVersionColumn>
          </React.Fragment>
        ))}
      </DoubleFieldGrid>

      {rowDataSourceValues.some((x) => x.dataSourceVersion?.isDisabled || x.dataSource.isDisabled) && (
        <ErrorAlertPanel className="w-full mt-4 mb-3">Please select a valid database version</ErrorAlertPanel>
      )}

      {rowDataSourceValues.length < 6 && <TextButton label="+ Add data source" onClick={createDataSourceRow} text />}
      <FieldContainer style={{ gap: '1rem', display: 'flex', flexDirection: 'column' }}>
        <CheckBoxGroup>
          <WizardCheckbox
            label="Other database"
            name="isOtherDatabaseUsedAsDatasource"
            value={epd?.isOtherDatabaseUsedAsDatasource ?? false}
            onChanged={onChangeEpd}
          />
        </CheckBoxGroup>

        {epd?.isOtherDatabaseUsedAsDatasource && (
          <WizardTextInputComponent
            label="Other database description"
            value={epd?.otherDatabaseUsedAsDatasourceDescription || ''}
            onChanged={onChangeEpd}
            name="otherDatabaseUsedAsDatasourceDescription"
            required={epd?.isOtherDatabaseUsedAsDatasource ?? false}
          />
        )}
      </FieldContainer>
    </>
  );
};

export default DataSourcesCopySection;

const CheckBoxGroup = styled.div`
  text-align: start;
  margin-top: 0.5rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  border-radius: 2px;

  & .p-checkbox {
    width: 16px;
    height: 16px;
  }

  & .p-checkbox .p-checkbox-box {
    width: 14px;
    height: 14px;
  }
`;

const DataSourceVersionColumn = styled.div`
  display: flex;
`;

function isNullOrWhiteSpace(str: string | undefined | null): boolean {
  return !str || str.trim().length === 0;
}
